import React from 'react'
import {
  FormProvider,
  useFieldArray,
  useFormContext,
  useWatch,
} from 'react-hook-form'
import {
  ActionIcon,
  Box,
  Button,
  Group,
  Stack,
  Text,
  Tooltip,
} from '@mantine/core'
import { Cross2Icon } from '@radix-ui/react-icons'
import { v4 as uuid } from 'uuid'

import { AttendeeForm } from '@/components/Event/NewScript/components/common/forms/AttendeeForm'
import { ActionContainer } from '@/components/Event/NewScript/components/common/ScriptTimeline/ActionContainer'
import { AttendeeRole, EventType } from '@/constants'
import { useModals } from '@/hooks/useModals'
import { Attendee, ScriptAttendee, ZoomParticipant } from '@/types'

const defaultDisabledWitnessRemovalTooltip =
  'Witnesses can only be removed before their witness section has started.'

export const isWitnessMissing = (
  attendees: Pick<ScriptAttendee, 'role'>[],
): boolean =>
  !attendees.some(attendee => attendee.role === AttendeeRole.WITNESS)

type AttendeeRemoveControlState = {
  disabled: boolean
  title: string
  tooltipLabel?: string
  visible: boolean
}

export const getAttendeeRemoveControlState = ({
  canRemoveWitnessAttendee,
  disabledWitnessRemovalTooltip = defaultDisabledWitnessRemovalTooltip,
  isBookedEventAttendee,
  isRemovableWitnessAttendee,
  isWitnessAttendee,
}: {
  canRemoveWitnessAttendee: boolean
  disabledWitnessRemovalTooltip?: string
  isBookedEventAttendee: boolean
  isRemovableWitnessAttendee: boolean
  isWitnessAttendee: boolean
}): AttendeeRemoveControlState => {
  if (isBookedEventAttendee && !canRemoveWitnessAttendee) {
    return {
      disabled: false,
      title: 'Remove attendee',
      visible: false,
    }
  }

  if (
    isWitnessAttendee &&
    !isRemovableWitnessAttendee &&
    !isBookedEventAttendee
  ) {
    return {
      disabled: true,
      title: 'Cannot remove witness',
      tooltipLabel: disabledWitnessRemovalTooltip,
      visible: true,
    }
  }

  return {
    disabled: false,
    title: 'Remove attendee',
    visible: true,
  }
}

export const AttendeesForm: React.FC<{
  zoomParticipants: ZoomParticipant[]
  eventType: EventType
  timezone: string
  eventAttendees: Attendee[]
  witnessAttendeeIds?: string[]
  previousWitnessAttendeeIds?: string[]
  subsequentWitnessAttendeeIds?: string[]
  removableWitnessAttendeeIds?: string[]
  disabledWitnessRemovalTooltip?: string
  onAddWitness?: () => void
  onRemoveWitnessAttendee?: (attendeeId: string) => void
}> = ({
  zoomParticipants,
  eventType,
  timezone,
  eventAttendees,
  witnessAttendeeIds = [],
  previousWitnessAttendeeIds = [],
  subsequentWitnessAttendeeIds = [],
  removableWitnessAttendeeIds = [],
  disabledWitnessRemovalTooltip = defaultDisabledWitnessRemovalTooltip,
  onAddWitness,
  onRemoveWitnessAttendee,
}) => {
  const methods = useFormContext<{ attendees: ScriptAttendee[] }>()

  const { fields, append, remove } = useFieldArray({
    control: methods.control,
    name: 'attendees',
  })

  const { openConfirmModal } = useModals()

  const attendees = useWatch({
    control: methods.control,
    name: 'attendees',
  })
  const witnessMissing = isWitnessMissing(attendees)

  const addNewAttendee = () => {
    const attendee = {
      attendee_id: uuid(),
      name: '',
      email: '',
      role: null,
      present: null,
      transcript_order: null,
      identity_document: null,
      zoom_participants: [],
      city: null,
      state: null,
    }
    // TODO: figure out how to address initial null value
    append(attendee as unknown as ScriptAttendee)
    methods.trigger('attendees')
  }

  const removeAttendeeHandler = (fieldId: string, attendee: ScriptAttendee) => {
    const removeAttendee = () => {
      remove(fields.findIndex(f => f.id === fieldId))
      methods.trigger('attendees')
    }

    const attendeeFromState = attendees.find(
      (a: ScriptAttendee) => a.attendee_id === attendee.attendee_id,
    )

    if (attendeeFromState !== undefined) {
      if (attendeeFromState.name !== '' || attendeeFromState.email !== '') {
        openConfirmModal({
          title: 'Are you sure?',
          onConfirm: removeAttendee,
          children: (
            <div>
              Are you sure you want to remove this attendee? This action is not
              reversible.
            </div>
          ),
          labels: {
            cancel: 'Cancel',
            confirm: 'Remove',
          },
        })
      } else {
        removeAttendee()
      }
    }
  }

  return (
    <Stack align="flex-start">
      <FormProvider {...methods}>
        {fields.map((field, number) => {
          const attendee = attendees[number] ?? field
          const isBookedEventAttendee = eventAttendees.some(
            e => e.id === field.attendee_id,
          )
          const isWitnessAttendee = witnessAttendeeIds.includes(
            field.attendee_id,
          )
          const witnessDepositionLabel =
            attendee.role === AttendeeRole.WITNESS
              ? null
              : previousWitnessAttendeeIds.includes(field.attendee_id)
                ? 'Witness in previous deposition'
                : subsequentWitnessAttendeeIds.includes(field.attendee_id)
                  ? 'Witness in subsequent deposition'
                  : null
          const isRemovableWitnessAttendee =
            removableWitnessAttendeeIds.includes(field.attendee_id)
          const canRemoveWitnessAttendee =
            isWitnessAttendee &&
            isRemovableWitnessAttendee &&
            onRemoveWitnessAttendee !== undefined
          const removeControl = getAttendeeRemoveControlState({
            canRemoveWitnessAttendee,
            disabledWitnessRemovalTooltip,
            isBookedEventAttendee,
            isRemovableWitnessAttendee,
            isWitnessAttendee,
          })

          return (
            <ActionContainer key={field.id}>
              {removeControl.visible && (
                <Tooltip
                  disabled={!removeControl.tooltipLabel}
                  label={removeControl.tooltipLabel}
                  withinPortal
                  withArrow
                >
                  <Box
                    component="span"
                    sx={theme => ({
                      display: 'inline-flex',
                      position: 'absolute',
                      right: theme.spacing.xs,
                      top: theme.spacing.xs,
                    })}
                  >
                    <ActionIcon
                      aria-label={removeControl.title}
                      color="primary"
                      disabled={removeControl.disabled}
                      title={removeControl.title}
                      variant="light"
                      sx={theme => ({
                        '&:not(:disabled):hover': {
                          backgroundColor: theme.colors.indigo[0],
                        },
                      })}
                      onClick={() =>
                        canRemoveWitnessAttendee
                          ? onRemoveWitnessAttendee(field.attendee_id)
                          : removeAttendeeHandler(field.id, field)
                      }
                    >
                      <Cross2Icon />
                    </ActionIcon>
                  </Box>
                </Tooltip>
              )}
              <Stack>
                {witnessDepositionLabel && (
                  <Text size="sm" fw={600} color="indigo.7">
                    {witnessDepositionLabel}
                  </Text>
                )}
                <AttendeeForm
                  eventType={eventType}
                  timezone={timezone}
                  namePrefix={`attendees.${number}`}
                  zoomParticipants={zoomParticipants}
                  isWitnessLocationEnabled={false}
                  witnessRole={witnessMissing ? 'selectable' : 'hidden'}
                  allAttendees={attendees}
                />
              </Stack>
            </ActionContainer>
          )
        })}
      </FormProvider>
      <Group spacing="sm">
        <Button variant="outline" compact onClick={addNewAttendee}>
          Add new attendee
        </Button>
        {onAddWitness && (
          <Button
            variant="subtle"
            compact
            disabled={witnessMissing}
            onClick={onAddWitness}
          >
            Add new witness
          </Button>
        )}
      </Group>
    </Stack>
  )
}
